import React from 'react';
import { Check, FileText, Layout, Edit3, Eye } from 'lucide-react';

const STEPS = {
    ENTRY_TYPE: 'step_1_entry_type',
    TEMPLATE: 'step_3_template',
    DETAILS: 'step_4_details',
    PREVIEW: 'step_5_preview'
};

const BookkeepingProgressTracker = ({
    activeStep,
    onStepClick,
    canNavigateToStep,
    isStepCompleted
}) => {
    const steps = [
        {
            id: 1,
            title: 'Start',
            description: 'Välj väg',
            icon: FileText,
            key: STEPS.ENTRY_TYPE
        },
        {
            id: 3,
            title: 'Mall',
            description: 'Välj, sök eller visa alla',
            icon: Layout,
            key: STEPS.TEMPLATE
        },
        {
            id: 4,
            title: 'Detaljer',
            description: 'Fyll i postens information',
            icon: Edit3,
            key: STEPS.DETAILS
        },
        {
            id: 5,
            title: 'Granska',
            description: 'Kontrollera och skapa',
            icon: Eye,
            key: STEPS.PREVIEW
        }
    ];

    const getStatus = (step) => {
        if (isStepCompleted(step.key)) return 'completed';
        if (activeStep === step.key) return 'current';
        return 'pending';
    };

    const isStepAccessible = (stepKey) => {
        return canNavigateToStep(stepKey);
    };

    return (
        <>
            <style>{`
                .bk-progress-tracker {
                    background: white;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    margin-bottom: 1.25rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e5e7eb;
                }

                .bk-step-indicator {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    font-weight: 600;
                    font-size: 0.875rem;
                    transition: all 0.3s ease;
                    flex-shrink: 0;
                }

                .bk-step-indicator.completed {
                    background: var(--bs-success);
                    color: white;
                }

                .bk-step-indicator.current {
                    background: var(--bs-primary);
                    color: white;
                    animation: bk-pulse 2s infinite;
                }

                .bk-step-indicator.pending {
                    background: #f3f4f6;
                    color: #6b7280;
                }

                .bk-step-connector {
                    flex: 1;
                    height: 2px;
                    background: #e5e7eb;
                    margin: 0 0.75rem;
                    transition: background 0.3s ease;
                }

                .bk-step-connector.completed {
                    background: var(--bs-primary);
                }

                @keyframes bk-pulse {
                    0% {
                        box-shadow: 0 0 0 0 rgba(68, 129, 78, 0.4);
                    }
                    70% {
                        box-shadow: 0 0 0 10px rgba(68, 129, 78, 0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(68, 129, 78, 0);
                    }
                }

                .bk-step-block {
                    cursor: default;
                }

                .bk-step-block.accessible {
                    cursor: pointer;
                }

                .bk-step-block.accessible:hover .bk-step-label {
                    color: var(--bs-primary);
                }

                .bk-step-label {
                    font-weight: 600;
                    font-size: 0.875rem;
                    color: #1e293b;
                    transition: color 0.2s ease;
                }

                .bk-step-sublabel {
                    font-size: 0.75rem;
                    color: #6b7280;
                }

                .bk-step-block.completed .bk-step-label {
                    color: #065f46;
                }

                .bk-step-block.current .bk-step-label {
                    color: var(--bs-primary);
                }

                .bk-step-block.pending .bk-step-label {
                    color: #9ca3af;
                }

                .bk-step-block.pending .bk-step-sublabel {
                    color: #d1d5db;
                }

                @media (max-width: 768px) {
                    .bk-steps-row {
                        flex-direction: column !important;
                        gap: 0.5rem;
                    }

                    .bk-step-connector {
                        display: none;
                    }

                    .bk-step-block {
                        background: #f8fafc;
                        border-radius: 8px;
                        border: 1px solid #e5e7eb;
                        padding: 0.75rem 1rem;
                    }
                }
            `}</style>

            <div className="bk-progress-tracker">
                <div className="d-flex align-items-center justify-content-between bk-steps-row">
                    {steps.map((step, index) => {
                        const status = getStatus(step);
                        const accessible = isStepAccessible(step.key);
                        const IconComponent = step.icon;

                        return (
                            <React.Fragment key={step.key}>
                                <div
                                    className={`d-flex align-items-center bk-step-block ${status} ${accessible ? 'accessible' : ''}`}
                                    onClick={() => accessible && onStepClick(step.key)}
                                >
                                    <div className={`bk-step-indicator ${status}`}>
                                        {status === 'completed' ? (
                                            <Check size={20} />
                                        ) : (
                                            <IconComponent size={18} />
                                        )}
                                    </div>
                                    <div className="ms-3">
                                        <div className="bk-step-label">{step.title}</div>
                                        <div className="bk-step-sublabel">{step.description}</div>
                                    </div>
                                </div>

                                {index < steps.length - 1 && (
                                    <div className={`bk-step-connector ${status === 'completed' ? 'completed' : ''}`} />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default BookkeepingProgressTracker;
